import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import { withStyles } from "@material-ui/core/styles";
import classNames from "classnames";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import FormGroup from "@material-ui/core/FormGroup";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Fade from "@material-ui/core/Fade";
import ButtonBase from "@material-ui/core/ButtonBase";
import CloseIcon from "@material-ui/icons/Close";

import SearchByMotoTireSize from "./SearchByMotoTireSize";
import OurBrands from "./OurBrands";
import ThreePilars from "./ThreePilars";
import TopSellers from "./TopSellers";
import SalesPromoBox from "./SalesPromoBox";

const baseCoverUrl =
  "https://s3.eu-west-2.amazonaws.com/sportyshop/catalog/homepage/covers/";
const mobile = ["cover.jpeg"];
const desktop = [
  "desktop/32002310_m.jpg",
  "desktop/81789642_m.jpg",
  "desktop/99150499_m.jpg",
  "desktop/99150501_m.jpg",
];

const homepageVideoUrl =
  "https://sportyshop.s3.eu-west-2.amazonaws.com/catalog/video/sportyshop-homepage.mp4";

export const MOBILE_POPULAR_SIZES_COLLAPSED_LIMIT = 3;

export function getMobilePopularSizes(popularSizes = [], showAllPopularSizes) {
  return showAllPopularSizes
    ? popularSizes
    : popularSizes.slice(0, MOBILE_POPULAR_SIZES_COLLAPSED_LIMIT);
}

function isSizeComplete(width, profile, rim) {
  return Boolean(width) && Boolean(profile) && Boolean(rim);
}

function formatSize(size) {
  return `${size.width}/${size.profile}-${size.rim}`;
}

function getSearchButtonText(setSearchOpen, tireCount) {
  if (setSearchOpen) {
    if (tireCount === 0) return "0 סטים";
    if (tireCount > 0) return `${tireCount} סטים`;
    return "חפש סט";
  }
  if (tireCount === 0) return "0 תוצאות";
  if (tireCount > 0) return `${tireCount} תוצאות`;
  return "חפש צמיגים";
}

const styles = (theme) => ({
  root: {
    paddingTop: 12,
    paddingBottom: 12,
    marginTop: 0,
    width: "initial",
    marginLeft: "auto",
    marginRight: "auto",
    // marginLeft: 0,
    // marginRight: 0,

    paddingLeft: theme.spacing(1.25),
    paddingRight: theme.spacing(1.25),
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: 16,
      paddingBottom: 16,
    },
  },
  homepageRoot: {
    marginTop: 8,
  },
  button: {
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  banner: {
    display: "none",
  },
  imageBackdrop: {
    display: "block",
    height: 196,
    backgroundColor: theme.palette.common.black,
    opacity: 0.4,
    transition: theme.transitions.create("opacity"),
  },
  sloganContainer: {
    justifyContent: "center",
    display: "flex",
    flexFlow: "column",
    position: "absolute",
    cursor: "default",
    top: 0,
    left: 0,
    height: 196,
  },
  homeContainer: {
    display: "block",
    width: "calc(100% - 24px)",
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: 47,
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      width: "90%",
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  aiWizardSection: {
    textAlign: "center",
    width: "100%",
    boxSizing: "border-box",
    marginLeft: "auto",
    marginRight: "auto",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}`,
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(4),
      margin: theme.spacing(2, 0, 3, 0),
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4, 5),
      margin: theme.spacing(3, 0, 4, 0),
    },
  },
  aiWizardTitle: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem",
      marginBottom: theme.spacing(1.5),
    },
  },
  aiWizardSubtitle: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      fontSize: "1rem",
      marginBottom: theme.spacing(3),
      maxWidth: "80%",
      margin: "0 auto",
      marginBottom: theme.spacing(3),
    },
  },
  aiWizardButton: {
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
    color: "white",
    fontWeight: "bold",
    padding: theme.spacing(1.5, 3),
    fontSize: "0.9rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "44px",
    "&:hover": {
      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[8],
    },
    transition: "all 0.3s ease",
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(1.5, 4),
      fontSize: "1rem",
    },
  },
  dividerSection: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(3, 0),
    [theme.breakpoints.up("md")]: {
      margin: theme.spacing(4, 0),
    },
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: theme.palette.grey[300],
  },
  dividerText: {
    padding: theme.spacing(0, 2),
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    fontWeight: 500,
  },
  // Desktop unified layout styles
  bottomRow: {
    display: "flex",
    gap: theme.spacing(4),
    width: "100%",
    marginTop: theme.spacing(3),
  },
  leftHalf: {
    flex: "1 1 50%",
    paddingRight: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    "& .MuiFormGroup-root": {
      marginTop: theme.spacing(2),
    },
  },
  rightHalf: {
    flex: "1 1 50%",
    paddingLeft: theme.spacing(2),
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
  },
  buttonContainer: {
    marginTop: "auto",
    paddingTop: theme.spacing(1.2),
    display: "flex",
    justifyContent: "center",
    "& .MuiButton-root": {
      width: "280px",
    },
  },
  wizardBox: {
    textAlign: "center",
    padding: theme.spacing(1.2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `3px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[4],
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    "& .MuiTypography-root": {
      marginBottom: theme.spacing(0.8),
    },
    "& .MuiButton-root": {
      marginTop: theme.spacing(0.8),
      width: "280px",
    },
  },
  poweredBySection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(1.5),
    fontSize: "0.75rem",
    color: theme.palette.grey[600],
    fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  openaiLogo: {
    width: "16px",
    height: "16px",
    marginLeft: theme.spacing(0.5),
    opacity: 0.7,
  },
  searchBox: {
    textAlign: "center",
    padding: theme.spacing(1.2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `3px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[4],
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    "& .MuiFormGroup-root": {
      marginTop: theme.spacing(0.8),
    },
  },
  sizeSelectors: {
    width: "100%",
  },
  formGroupFull: {
    width: "100%",
  },
  popularSetChip: {
    width: 244,
    height: 42,
    margin: "6px",
    borderRadius: 21,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    color: theme.palette.common.white,
    boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
    transition: "box-shadow 0.15s ease-in-out",
    "&:hover, &:focus": {
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
      boxShadow: "0 5px 16px rgba(0,0,0,0.22)",
    },
    "& .MuiChip-label": {
      fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: "1rem",
      fontWeight: 700,
      letterSpacing: 0.5,
      textShadow: "0 1px 2px rgba(0,0,0,0.2)",
    },
  },
  popularSetChipMobile: {
    width: "100%",
    maxWidth: 280,
    margin: "4px auto",
  },
  popularSetPlus: {
    margin: "0 6px",
    opacity: 0.85,
  },
  hiddenPromo: {
    display: "none",
  },
  leftHalfFull: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  setModeToggleRow: {
    width: "100%",
    textAlign: "center",
    marginTop: theme.spacing(1),
  },
  setModeToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "5px 14px 5px 5px",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 20,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: "0.85rem",
    fontWeight: 600,
    lineHeight: 1.5,
    transition: "background-color 0.15s ease-in-out",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  newBadge: {
    padding: "1px 8px",
    borderRadius: 12,
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
    color: theme.palette.common.white,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  setHeader: {
    position: "relative",
  },
  backToSingle: {
    position: "absolute",
    top: "50%",
    right: 0,
    transform: "translateY(-50%)",
    minWidth: 0,
    padding: "2px 8px",
    fontSize: "0.8rem",
    fontWeight: 500,
    textTransform: "none",
    color: theme.palette.text.secondary,
  },
  setPanels: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(1.5),
    width: "100%",
  },
  setPanelsMobile: {
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  setPanelsJoin: {
    alignSelf: "center",
    flex: "0 0 auto",
    width: 26,
    height: 26,
    lineHeight: "26px",
    borderRadius: "50%",
    textAlign: "center",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[200],
  },
  axlePanel: {
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    padding: theme.spacing(1, 1.5, 0.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderTopWidth: 3,
    borderRadius: theme.spacing(1),
    "&& form": {
      display: "block",
      width: "100%",
      maxWidth: "none",
    },
    "&& form > .MuiFormGroup-root": {
      flexDirection: "row",
      flexWrap: "nowrap",
      alignItems: "flex-end",
      gap: theme.spacing(1),
      width: "100%",
      margin: 0,
    },
    "&& .MuiFormControl-root": {
      flex: "1 1 0",
      width: "auto",
      minWidth: 0,
      maxWidth: "none",
      margin: theme.spacing(0.5, 0),
    },
  },
  axlePanelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 24,
  },
  axlePanelLabel: {
    fontSize: "0.85rem",
    fontWeight: 700,
  },
  axlePanelSize: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  axlePanelFront: {
    borderTopColor: theme.palette.primary.main,
    "& $axlePanelLabel": {
      color: theme.palette.primary.main,
    },
  },
  axlePanelRear: {
    borderTopColor: theme.palette.grey[800],
    "& $axlePanelLabel": {
      color: theme.palette.grey[800],
    },
  },
  desktopAiWizardSection: {
    textAlign: "center",
    padding: theme.spacing(4, 3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `3px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[6],
    height: "fit-content",
    "& .MuiTypography-root": {
      marginBottom: theme.spacing(2),
    },
    "& .MuiButton-root": {
      marginTop: theme.spacing(1),
    },
  },
  homeContainerDesktop: {
    display: "none",
  },
  pillarsContainer: {
    background: "#fff",
  },
  topSellerInnerContainer: {
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  recentSizesContainer: {},
  formGroup: {
    display: "block",
    marginTop: 0,
    marginLeft: "auto",
    marginRight: "auto",
  },
  popularSizesCont: {
    margin: "16px 8px 24px 8px",
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "4px",
    [theme.breakpoints.up("md")]: {
      margin: "20px 8px 28px 8px",
      gap: "6px",
    },
  },
  mobilePopularSizesCont: {
    margin: "8px auto 8px auto",
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "4px",
    maxWidth: 360,
  },
  videoContainer: {
    position: "absolute",
    height: "100%",
    width: "100%",
    overflow: "hidden",
  },
  overlayVideo: {
    position: "relative",
    height: 196,
    minWidth: "100%",
    minHeight: "100%",
    objectFit: "cover",
  },
  pillarsDesktop: {
    display: "none",
  },
  pillarsMobile: {
    display: "flex",
    width: "100%",
  },
  popularSize: {
    margin: "6px 8px",
    fontSize: "1rem",
    fontWeight: "700",
    fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
    cursor: "pointer",
    padding: "12px 20px",
    width: 152,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    border: "none",
    borderRadius: "25px",
    color: "white",
    boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
      transition: "left 0.5s",
    },
    "&:hover": {
      transform: "translateY(-3px) scale(1.05)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.25)",
      "&:before": {
        left: "100%",
      },
    },
    "&:active": {
      transform: "translateY(-1px) scale(1.02)",
    },
    "& .MuiChip-label": {
      fontSize: "1rem",
      fontWeight: "700",
      fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
      letterSpacing: "1px",
      textShadow: "0 1px 2px rgba(0,0,0,0.2)",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.1rem",
      padding: "14px 24px",
      margin: "8px 10px",
      "& .MuiChip-label": {
        fontSize: "1.1rem",
      },
    },
  },
  mobilePopularSize: {
    width: 138,
    margin: "5px 4px",
    padding: "10px 14px",
    "&:hover": {
      transform: "none",
      boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
    },
    "&:active": {
      transform: "none",
    },
    "& .MuiChip-label": {
      fontSize: "0.95rem",
    },
  },
  mobilePopularSizesToggle: {
    display: "flex",
    margin: "4px auto 18px auto",
    padding: theme.spacing(0.5, 1.5),
    minHeight: 36,
    fontSize: "0.9rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  mobileSearchSection: {
    width: "100%",
    maxWidth: 360,
    boxSizing: "border-box",
    margin: "18px auto 0 auto",
    padding: theme.spacing(2, 1.5, 2.5),
    textAlign: "center",
    backgroundColor: theme.palette.grey[50],
    borderTop: `3px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
  },
  mobileSearchTitle: {
    marginTop: 0,
    marginBottom: theme.spacing(1.5),
  },
  mobileSearchFormGroup: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: "0 auto",
    "& form": {
      width: "100%",
      maxWidth: 300,
      margin: "0 auto",
      display: "flex",
      justifyContent: "center",
    },
    "& form > .MuiFormGroup-root": {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      margin: "0 auto",
    },
    "& .MuiFormControl-root": {
      width: "100%",
      maxWidth: 274,
      margin: `${theme.spacing(0.75)}px auto`,
    },
  },
  mobileSearchButton: {
    width: "100%",
    maxWidth: 280,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: theme.spacing(1.5),
  },
  homeBoxTitleMobile: {
    fontSize: "1.2rem",
    fontWeight: "bold",
    textAlign: "center",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  "@media (min-width: 1024px)": {
    recentSizesContainer: {
      display: "none",
    },
    desktopSubmitButton: {
      width: 240,
      margin: "12px auto",
    },
    formGroup: {
      display: "flex",
      justifyContent: "center",
    },
    pillarsMobile: {
      display: "none",
    },
    pillarsDesktop: {
      display: "block",
    },
    popularSize: {
      width: 170,
    },
    homeBoxTitle: {
      fontSize: "2rem",
      fontWeight: "bold",
      textAlign: "center",
      color: theme.palette.primary.main,
      margin: "0 0 16px 0",
      padding: "0 0 12px 0",
      borderBottom: `2px solid ${theme.palette.grey[200]}`,
      fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
      [theme.breakpoints.up("md")]: {
        fontSize: "2.2rem",
        margin: "0 0 20px 0",
        padding: "0 0 16px 0",
      },
    },
    sloganDesktop: {
      background: "#4f4f4f",
      padding: "0 8px",
      fontSize: "50px",
      textShadow: "4px 0px 7px rgba(42, 45, 51, 0.5)",
      fontWeight: "540",
      color: "#fefefe",
    },
    sloganSubtitleDesktop: {
      background: "#4f4f4f",
      padding: "0 8px",
      fontSize: "24px",
      fontWeight: "540",
      color: "#ffe89c",
    },
    sloganContainerDesktop: {
      width: 1600,
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "right",
      position: "relative",
      top: 25,
      marginLeft: "auto",
      marginRight: "auto",
    },
    homeContainerDesktop: {
      display: "block",
      position: "relative",
      width: 1600,
      marginLeft: "auto",
      marginRight: "auto",
    },
    banner: {
      display: "block",
      marginTop: 49,
      height: 772,
    },
    videoContainer: {
      position: "absolute",
      height: 772,
      width: "100%",
      overflow: "hidden",
    },
    overlayVideo: {
      position: "relative",
      top: 0,
      minWidth: "100%",
      minHeight: "100%",
      objectFit: "cover",
    },
    homeContainer: {
      display: "none",
    },
    pillarsContainer: {
      background: "#fff",
    },
    topSellerInnerContainer: {
      width: 1600,
      marginLeft: "auto",
      marginRight: "auto",
    },
    pillarsInnerContainer: {
      width: 1600,
      marginLeft: "auto",
      marginRight: "auto",
    },
    root: {
      position: "absolute",
      width: 900,
      top: 35,
      left: "50%",
      transform: "translateX(-50%)",
      padding: theme.spacing(4),
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(1),
    },
    sloganContainer: {
      display: "none",
    },
    imageBackdrop: {
      display: "none",
    },
  },

  slogan: {
    padding: "0 8px 0 8px",
    textAlign: "center",
    fontSize: "17pt",
    margin: "12px 0",
    textShadow: "4px 0px 7px rgba(42, 45, 51, 0.5)",
    fontWeight: "540",
    color: "#fefefe",
  },
  subtitleSlogan: {
    margin: "0 20px",
    textAlign: "left",
    fontSize: 13,
    fontWeight: "540",
    color: "#ffe89c",
  },
  stepsRoot: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  homeH3: {
    fontFamily: '"Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: "1.1rem",
    fontWeight: "600",
    color: "#555555",
    marginTop: 20,
    marginBottom: 16,
    [theme.breakpoints.up("md")]: {
      fontSize: "1.2rem",
      marginTop: 24,
    },
  },
  stepChip: {
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    fontSize: 15,
    color: "#fefefe",
  },
  sloganButton: {
    border: "solid 2px #fefefe",
    margin: "10px auto",
    width: "50%",
    color: "#fefefe",
  },
});

const hebrewLabels = {
  slogan1: "צמיגים לאופנוע בזול - חפש, השווה וחסוך",
  subtitle:
    "בחר ממגוון צמיגים רחב, השווה התקנה במגוון מוסכים ברחבי הארץ כולל תור בקליק.",
  subtitle2: "המחיר הזול ביותר מובטח",
  titles: {
    popularSizes: "מידות פופלריות",
    popularSets: "סטים פופולריים",
    searchBySize: "חיפוש לפי מידת צמיג",
    searchBySet: "חיפוש סט צמיגים",
    homeJumboTitle: "מצא את הצמיגים המושלמים לאופנוע שלך",
  },
  aiWizard: {
    title: "חדש! אשף הצמיגים החכם",
    subtitle: "תן לבינה המלאכותית שלנו לעזור לך למצוא את הצמיגים המושלמים",
    cta: "התחל עם האשף החכם",
    orText: "או חפש בצורה המסורתית:",
  },
  bannerCTA: "מידע נוסף",
  setSearch: {
    newBadge: "חדש",
    toggle: "חיפוש סט: קדמי + אחורי",
    backToSingle: "צמיג בודד",
    front: "צמיג קדמי",
    rear: "צמיג אחורי",
  },
};

class HomePage extends React.Component {
  constructor() {
    super();
    this.state = {
      showAllPopularSizes: false,
    };
  }

  componentDidMount() {
    this.props.actions.getTopSellers();
    this.props.actions.getPopularSizes();
    this.props.actions.getPopularTireSets();
    this.props.actions.loadTireDimensionsWidths();
    // Only fetch promo when SSR didn't already roll a display decision
    // (i.e. client-side navigation without a preloaded state)
    if (!this.props.catalog.homepagePromo || !this.props.catalog.homepagePromo.loaded) {
      this.props.actions.getHomepagePromo();
    }
  }

  componentDidUpdate(prevProps) {
    const { catalog, actions } = this.props;
    const prev = prevProps.catalog;

    const frontRimInvalid =
      catalog.rims !== prev.rims &&
      catalog.selectedRim &&
      catalog.rims.length > 0 &&
      !catalog.rims.includes(catalog.selectedRim);
    const rearRims = catalog.rearRims || [];
    const rearRimInvalid =
      catalog.rearRims !== prev.rearRims &&
      catalog.rearSelectedRim &&
      rearRims.length > 0 &&
      !rearRims.includes(catalog.rearSelectedRim);

    if (frontRimInvalid) actions.selectRim("");
    if (rearRimInvalid) actions.selectRearRim("");
    if (frontRimInvalid || rearRimInvalid) return;

    const frontChanged =
      prev.selectedWidth !== catalog.selectedWidth ||
      prev.selectedProfile !== catalog.selectedProfile ||
      prev.selectedRim !== catalog.selectedRim;
    const rearChanged =
      prev.rearSelectedWidth !== catalog.rearSelectedWidth ||
      prev.rearSelectedProfile !== catalog.rearSelectedProfile ||
      prev.rearSelectedRim !== catalog.rearSelectedRim;
    const setModeChanged = prev.setSearchOpen !== catalog.setSearchOpen;
    const frontComplete = isSizeComplete(
      catalog.selectedWidth,
      catalog.selectedProfile,
      catalog.selectedRim
    );
    const rearComplete = isSizeComplete(
      catalog.rearSelectedWidth,
      catalog.rearSelectedProfile,
      catalog.rearSelectedRim
    );

    if (catalog.setSearchOpen) {
      if (
        frontComplete &&
        catalog.frontTouched &&
        !catalog.rearTouched &&
        (frontChanged || setModeChanged)
      ) {
        actions.loadSizeSuggestions("front", {
          width: catalog.selectedWidth,
          profile: catalog.selectedProfile,
          rim: catalog.selectedRim,
        });
      }
      if (
        rearComplete &&
        catalog.rearTouched &&
        !catalog.frontTouched &&
        (rearChanged || setModeChanged)
      ) {
        actions.loadSizeSuggestions("rear", {
          width: catalog.rearSelectedWidth,
          profile: catalog.rearSelectedProfile,
          rim: catalog.rearSelectedRim,
        });
      }
    }

    if (frontChanged || (catalog.setSearchOpen && rearChanged) || setModeChanged) {
      this.resetValidationState();
      // Count results as soon as a size is complete, including when the rim is
      // auto-selected (e.g. 180/55 has only 17), so the button shows the count
      // and a single click opens the results.
      if (!catalog.setSearchOpen && frontComplete) {
        actions.validateTireAvailability(
          catalog.selectedWidth,
          catalog.selectedProfile,
          catalog.selectedRim
        );
      }
      if (catalog.setSearchOpen && frontComplete && rearComplete) {
        actions.validateTirePairAvailability(
          {
            width: catalog.selectedWidth,
            profile: catalog.selectedProfile,
            rim: catalog.selectedRim,
          },
          {
            width: catalog.rearSelectedWidth,
            profile: catalog.rearSelectedProfile,
            rim: catalog.rearSelectedRim,
          }
        );
      }
    }
  }

  resetValidationState = () => {
    this.props.actions.resetTireValidation();
  };

  handleFrontWidthChange = (value) => {
    const { actions } = this.props;
    actions.setFrontTouched(true);
    actions.resetDimensionsMoto();
    actions.selectWidth(value);
    if (value) {
      actions.loadTireProfilesByWidth(value);
    }
  };

  handleFrontProfileChange = (value) => {
    const { actions, catalog } = this.props;
    actions.setFrontTouched(true);
    actions.selectProfile(value);
    if (catalog.selectedWidth && value) {
      actions.loadTireRimsByWidthAndProfile(catalog.selectedWidth, value);
    }
  };

  handleFrontRimChange = (rimValue) => {
    const { actions } = this.props;
    actions.setFrontTouched(true);
    actions.selectRim(rimValue);
  };

  handleRearWidthChange = (value) => {
    const { actions } = this.props;
    actions.setRearTouched(true);
    actions.resetRearDimensions();
    actions.selectRearWidth(value);
    if (value) {
      actions.loadTireProfilesByWidth(value, "rear");
    }
  };

  handleRearProfileChange = (value) => {
    const { actions, catalog } = this.props;
    actions.setRearTouched(true);
    actions.selectRearProfile(value);
    actions.selectRearRim("");
    if (catalog.rearSelectedWidth && value) {
      actions.loadTireRimsByWidthAndProfile(
        catalog.rearSelectedWidth,
        value,
        "rear"
      );
    }
  };

  handleRearRimChange = (value) => {
    this.props.actions.setRearTouched(true);
    this.props.actions.selectRearRim(value);
  };

  openSetSearch = () => {
    this.props.actions.openSetSearch();
  };

  closeSetSearch = () => {
    this.props.actions.closeSetSearch();
  };

  validateForm = (e) => {
    e.preventDefault();

    const { catalog } = this.props;
    const {
      selectedWidth,
      selectedProfile,
      selectedRim,
      rearSelectedWidth,
      rearSelectedProfile,
      rearSelectedRim,
      hasAvailableTires,
      tireCount,
      setSearchOpen,
    } = catalog;

    // One click always goes to the results. The count is shown on the button
    // beforehand (see componentDidUpdate); only a confirmed 0 blocks the click.
    if (this.isSearchSubmitDisabled()) {
      return;
    }
    if (hasAvailableTires === false && tireCount === 0) {
      return;
    }

    if (setSearchOpen) {
      this.props.history.push(
        `/list/moto/set/${selectedWidth}/${selectedProfile}/${selectedRim}/${rearSelectedWidth}/${rearSelectedProfile}/${rearSelectedRim}`
      );
      return;
    }

    this.props.history.push(
      `/list/moto/${selectedWidth}/${selectedProfile}/${selectedRim}`
    );
  };

  toggleMobilePopularSizes = () => {
    this.setState((prevState) => ({
      showAllPopularSizes: !prevState.showAllPopularSizes,
    }));
  };

  isSearchSubmitDisabled = () => {
    const { catalog } = this.props;
    const frontComplete = isSizeComplete(
      catalog.selectedWidth,
      catalog.selectedProfile,
      catalog.selectedRim
    );
    const rearComplete = isSizeComplete(
      catalog.rearSelectedWidth,
      catalog.rearSelectedProfile,
      catalog.rearSelectedRim
    );
    const incomplete = catalog.setSearchOpen
      ? !frontComplete || !rearComplete
      : !frontComplete;
    return incomplete || catalog.tireValidationPending;
  };

  renderSearchHeader = (titleClassName) => {
    const { classes, catalog } = this.props;

    if (!catalog.setSearchOpen) {
      return <h3 className={titleClassName}>{hebrewLabels.titles.searchBySize}</h3>;
    }

    return (
      <div className={classes.setHeader}>
        <h3 className={titleClassName}>{hebrewLabels.titles.searchBySet}</h3>
        <Button
          type="button"
          size="small"
          className={classes.backToSingle}
          onClick={this.closeSetSearch}
          startIcon={<CloseIcon fontSize="small" />}
        >
          {hebrewLabels.setSearch.backToSingle}
        </Button>
      </div>
    );
  };

  renderAxlePanel = (layout, axle) => {
    const { classes, catalog } = this.props;
    const isFront = axle === "front";
    const size = isFront
      ? {
          width: catalog.selectedWidth,
          profile: catalog.selectedProfile,
          rim: catalog.selectedRim,
        }
      : {
          width: catalog.rearSelectedWidth,
          profile: catalog.rearSelectedProfile,
          rim: catalog.rearSelectedRim,
        };
    const sizeText = isSizeComplete(size.width, size.profile, size.rim)
      ? formatSize(size)
      : "";

    return (
      <div
        className={classNames(
          classes.axlePanel,
          isFront ? classes.axlePanelFront : classes.axlePanelRear
        )}
      >
        <div className={classes.axlePanelHeader}>
          <span className={classes.axlePanelLabel}>
            {isFront ? hebrewLabels.setSearch.front : hebrewLabels.setSearch.rear}
          </span>
          {sizeText ? <bdi className={classes.axlePanelSize}>{sizeText}</bdi> : null}
        </div>
        <SearchByMotoTireSize
          idPrefix={`${layout}-${axle}`}
          widths={catalog.widths}
          profiles={isFront ? catalog.profiles : catalog.rearProfiles}
          rims={isFront ? catalog.rims : catalog.rearRims}
          selectedWidth={size.width}
          selectedProfile={size.profile}
          selectedRim={size.rim}
          onWidthChange={isFront ? this.handleFrontWidthChange : this.handleRearWidthChange}
          onProfileChange={
            isFront ? this.handleFrontProfileChange : this.handleRearProfileChange
          }
          onRimChange={isFront ? this.handleFrontRimChange : this.handleRearRimChange}
        />
      </div>
    );
  };

  renderPopularTireSetChip = (tireSet, layout) => {
    const { classes, actions } = this.props;
    const rearText = formatSize(tireSet.rear);
    const frontText = formatSize(tireSet.front);

    return (
      <Chip
        key={tireSet.key}
        clickable
        onClick={() => actions.applyPopularTireSet(tireSet)}
        aria-label={`${hebrewLabels.setSearch.rear} ${rearText}, ${hebrewLabels.setSearch.front} ${frontText}`}
        className={classNames(
          classes.popularSetChip,
          layout === "mobile" && classes.popularSetChipMobile
        )}
        label={
          <span dir="ltr">
            {rearText}
            <span className={classes.popularSetPlus}>+</span>
            {frontText}
          </span>
        }
      />
    );
  };

  renderPopularSizeChip = (size, idx, extraClassName) => {
    const { classes } = this.props;

    return (
      <Chip
        key={idx}
        component={Link}
        to={`/list/moto/${size.width}/${size.profile}/${size.rim}`}
        className={classNames(classes.popularSize, extraClassName)}
        label={formatSize(size)}
      />
    );
  };

  renderSizeSelectors = (layout) => {
    const { classes, catalog } = this.props;

    if (!catalog.setSearchOpen) {
      return (
        <div className={classes.sizeSelectors}>
          <SearchByMotoTireSize
            idPrefix={`${layout}-single`}
            widths={catalog.widths}
            profiles={catalog.profiles}
            rims={catalog.rims}
            selectedWidth={catalog.selectedWidth}
            selectedProfile={catalog.selectedProfile}
            selectedRim={catalog.selectedRim}
            onWidthChange={this.handleFrontWidthChange}
            onProfileChange={this.handleFrontProfileChange}
            onRimChange={this.handleFrontRimChange}
          />
          <div className={classes.setModeToggleRow}>
            <ButtonBase className={classes.setModeToggle} onClick={this.openSetSearch}>
              <span className={classes.newBadge}>{hebrewLabels.setSearch.newBadge}</span>
              {hebrewLabels.setSearch.toggle}
            </ButtonBase>
          </div>
        </div>
      );
    }

    return (
      <Fade in timeout={250}>
        <div
          className={classNames(
            classes.setPanels,
            layout === "mobile" && classes.setPanelsMobile
          )}
        >
          {this.renderAxlePanel(layout, "front")}
          <span className={classes.setPanelsJoin} aria-hidden="true">
            +
          </span>
          {this.renderAxlePanel(layout, "rear")}
        </div>
      </Fade>
    );
  };

  renderSearchButton = (extraClassName) => {
    const { classes, catalog } = this.props;
    const { tireValidationPending, tireCount, setSearchOpen } = catalog;

    return (
      <Button
        type="button"
        variant="contained"
        disabled={this.isSearchSubmitDisabled() || tireCount === 0}
        color="primary"
        className={
          extraClassName
            ? classNames(classes.aiWizardButton, extraClassName)
            : classes.aiWizardButton
        }
        onClick={this.validateForm}
      >
        {tireValidationPending ? (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            בודק זמינות...
            <CircularProgress
              size={20}
              color="inherit"
              style={{ width: "1.5rem", height: "1.5rem" }}
            />
          </span>
        ) : (
          getSearchButtonText(setSearchOpen, tireCount)
        )}
      </Button>
    );
  };

  render() {
    const { classes, catalog } = this.props;
    const { showAllPopularSizes } = this.state;

    const {
      topSellers,
      popularSizes = [],
      popularTireSets = [],
      setSearchOpen,
      homepagePromo = { salePercent: 0, display: null, promos: [], loaded: false },
    } = catalog;

    const showSalePromo =
      homepagePromo.display === 'sale' && homepagePromo.promos && homepagePromo.promos.length > 0;
    const popularTitle = setSearchOpen
      ? hebrewLabels.titles.popularSets
      : hebrewLabels.titles.popularSizes;
    const popularOptions = setSearchOpen ? popularTireSets : popularSizes;
    const mobilePopularOptions = getMobilePopularSizes(
      popularOptions,
      showAllPopularSizes
    );
    const hasMorePopularSizes =
      popularOptions.length > MOBILE_POPULAR_SIZES_COLLAPSED_LIMIT;

    return (
      <div className={classes.homepageRoot}>
        <div className={classes.banner}>
          <div className={classes.videoContainer}>
            <video
              playsInline
              autoPlay
              muted
              loop
              className={classes.overlayVideo}
            >
              <source src={homepageVideoUrl} type="video/mp4" />
            </video>
          </div>

          <div className={classes.imageBackdrop} />

          <div className={classes.homeContainerDesktop}>
            <Paper className={classes.root} elevation={4}>
              {/* Top Section - 100% Width */}
              <Typography component="h3" className={classes.homeBoxTitle}>
                {hebrewLabels.titles.homeJumboTitle}
              </Typography>

              <h3 className={classes.homeH3}>{popularTitle}</h3>
              <div className={classes.popularSizesCont}>
                {setSearchOpen
                  ? popularTireSets.map((tireSet) =>
                      this.renderPopularTireSetChip(tireSet, "desktop")
                    )
                  : popularSizes.map((size, idx) =>
                      this.renderPopularSizeChip(size, idx)
                    )}
              </div>

              {/* Bottom Row - 50% 50% Layout */}
              <Box className={classes.bottomRow}>
                {/* Left 50% - Traditional Search */}
                <Box
                  className={classNames(
                    classes.leftHalf,
                    catalog.setSearchOpen && classes.leftHalfFull
                  )}
                >
                  <Box className={classes.searchBox}>
                    {this.renderSearchHeader(classes.homeH3)}
                    <FormGroup
                      row
                      className={classNames(
                        classes.formGroup,
                        setSearchOpen && classes.formGroupFull
                      )}
                    >
                      {this.renderSizeSelectors("desktop")}
                    </FormGroup>

                    {/* Traditional Search Button */}
                    <Box className={classes.buttonContainer}>
                      {this.renderSearchButton()}
                    </Box>
                  </Box>
                </Box>

                {/* Right 50% - AI Wizard or Sales Promo */}
                <Box
                  className={classNames(
                    classes.rightHalf,
                    catalog.setSearchOpen && classes.hiddenPromo
                  )}
                >
                  <Box className={classes.wizardBox}>
                    {showSalePromo ? (
                      <SalesPromoBox promos={homepagePromo.promos} />
                    ) : (
                      <>
                    <Typography className={classes.aiWizardTitle}>
                      {hebrewLabels.aiWizard.title}
                    </Typography>
                    <Typography className={classes.aiWizardSubtitle}>
                      {hebrewLabels.aiWizard.subtitle}
                    </Typography>
                    <Button
                      component={Link}
                      to="/find-the-right-tires-for-you"
                      variant="contained"
                      className={classes.aiWizardButton}
                      startIcon={
                        <svg
                          style={{ width: "18px", height: "18px" }}
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zm-2.4718-11.2491a4.4842 4.4842 0 0 1 2.3456-1.9714v5.8374a.7744.7744 0 0 0 .3927.6813l5.8428 3.3685-2.02 1.1686a.0757.0757 0 0 1-.071 0L2.8435 8.7762a4.4992 4.4992 0 0 1 .9313-5.2105zm16.5656 3.8558l-5.8428-3.3685 2.02-1.1686a.0757.0757 0 0 1 .071 0l4.8558 2.7956a4.4992 4.4992 0 0 1-.9254 8.1373v-5.8374a.7744.7744 0 0 0-.3927-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8558-2.7956a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1686a.0804.0804 0 0 1-.038-.0567V6.0593a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9944l-2.5974 1.4997-2.6067-1.4997Z" />
                        </svg>
                      }
                    >
                      {hebrewLabels.aiWizard.cta}
                    </Button>

                    <div className={classes.poweredBySection}>
                      <span>Powered by OpenAI</span>
                      <svg
                        className={classes.openaiLogo}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zm-2.4718-11.2491a4.4842 4.4842 0 0 1 2.3456-1.9714v5.8374a.7744.7744 0 0 0 .3927.6813l5.8428 3.3685-2.02 1.1686a.0757.0757 0 0 1-.071 0L2.8435 8.7762a4.4992 4.4992 0 0 1 .9313-5.2105zm16.5656 3.8558l-5.8428-3.3685 2.02-1.1686a.0757.0757 0 0 1 .071 0l4.8558 2.7956a4.4992 4.4992 0 0 1-.9254 8.1373v-5.8374a.7744.7744 0 0 0-.3927-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8558-2.7956a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1686a.0804.0804 0 0 1-.038-.0567V6.0593a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9944l-2.5974 1.4997-2.6067-1.4997Z" />
                      </svg>
                    </div>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </div>
        </div>

        <div className={classes.homeContainer}>
          <Paper className={classes.root} elevation={4}>
            <Typography component="h3" className={classes.homeBoxTitleMobile}>
              {hebrewLabels.titles.homeJumboTitle}
            </Typography>

            {/* AI Wizard CTA Section - Mobile (or Sales Promo) */}
            <Box className={classes.aiWizardSection}>
              {showSalePromo ? (
                <SalesPromoBox promos={homepagePromo.promos} />
              ) : (
                <>
                  <Typography className={classes.aiWizardTitle}>
                    {hebrewLabels.aiWizard.title}
                  </Typography>
                  <Typography className={classes.aiWizardSubtitle}>
                    {hebrewLabels.aiWizard.subtitle}
                  </Typography>
                  <Button
                    component={Link}
                    to="/find-the-right-tires-for-you"
                    variant="contained"
                    className={classes.aiWizardButton}
                    startIcon={
                      <svg
                        style={{ width: "18px", height: "18px" }}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zm-2.4718-11.2491a4.4842 4.4842 0 0 1 2.3456-1.9714v5.8374a.7744.7744 0 0 0 .3927.6813l5.8428 3.3685-2.02 1.1686a.0757.0757 0 0 1-.071 0L2.8435 8.7762a4.4992 4.4992 0 0 1 .9313-5.2105zm16.5656 3.8558l-5.8428-3.3685 2.02-1.1686a.0757.0757 0 0 1 .071 0l4.8558 2.7956a4.4992 4.4992 0 0 1-.9254 8.1373v-5.8374a.7744.7744 0 0 0-.3927-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8558-2.7956a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1686a.0804.0804 0 0 1-.038-.0567V6.0593a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9944l-2.5974 1.4997-2.6067-1.4997Z" />
                      </svg>
                    }
                  >
                    {hebrewLabels.aiWizard.cta}
                  </Button>

                  <div className={classes.poweredBySection}>
                    <span>Powered by OpenAI</span>
                    <svg
                      className={classes.openaiLogo}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4742 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zm-2.4718-11.2491a4.4842 4.4842 0 0 1 2.3456-1.9714v5.8374a.7744.7744 0 0 0 .3927.6813l5.8428 3.3685-2.02 1.1686a.0757.0757 0 0 1-.071 0L2.8435 8.7762a4.4992 4.4992 0 0 1 .9313-5.2105zm16.5656 3.8558l-5.8428-3.3685 2.02-1.1686a.0757.0757 0 0 1 .071 0l4.8558 2.7956a4.4992 4.4992 0 0 1-.9254 8.1373v-5.8374a.7744.7744 0 0 0-.3927-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8558-2.7956a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1686a.0804.0804 0 0 1-.038-.0567V6.0593a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9944l-2.5974 1.4997-2.6067-1.4997Z" />
                    </svg>
                  </div>
                </>
              )}
            </Box>

            {/* Separator - Mobile */}
            <Box className={classes.dividerSection}>
              <Box className={classes.dividerLine} />
              <Typography className={classes.dividerText}>
                {hebrewLabels.aiWizard.orText}
              </Typography>
              <Box className={classes.dividerLine} />
            </Box>

            <h3 className={classes.homeH3}>{popularTitle}</h3>

            <div className={classes.mobilePopularSizesCont}>
              {mobilePopularOptions.map((option, idx) =>
                setSearchOpen
                  ? this.renderPopularTireSetChip(option, "mobile")
                  : this.renderPopularSizeChip(option, idx, classes.mobilePopularSize)
              )}
            </div>

            {hasMorePopularSizes && (
              <Button
                type="button"
                className={classes.mobilePopularSizesToggle}
                onClick={this.toggleMobilePopularSizes}
              >
                {showAllPopularSizes ? "הצג פחות מידות" : "הצג עוד מידות"}
              </Button>
            )}

            <Box className={classes.mobileSearchSection}>
              {this.renderSearchHeader(
                classNames(classes.homeH3, classes.mobileSearchTitle)
              )}
              <FormGroup row className={classes.mobileSearchFormGroup}>
                {this.renderSizeSelectors("mobile")}
                {this.renderSearchButton(classes.mobileSearchButton)}
              </FormGroup>
            </Box>
          </Paper>
        </div>

        <div className={classes.pillarsContainer}>
          <div className={classes.topSellerInnerContainer}>
            <TopSellers data={topSellers} />
          </div>
        </div>

        <ThreePilars className={classNames(classes.pillarsContainer)} />

        <div className={classes.pillarsContainer}>
          <div className={classes.topSellerInnerContainer}>
            <OurBrands />
          </div>
        </div>
      </div>
    );
  }
}

HomePage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withStyles(styles)(HomePage);
